import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, ChevronRight } from "lucide-react";
import { ListInput } from "./ListInput";
import type { QuestionnaireData } from "./index";

interface QuestionnaireStepProps {
  data: QuestionnaireData;
  onChange: (data: QuestionnaireData) => void;
  onNext: () => void;
  onBack: () => void;
}

interface Question {
  field: keyof QuestionnaireData;
  title: string;
  description: string;
  placeholder: string;
}

const QUESTIONS: Question[] = [
  {
    field: "deliverables",
    title: "Deliverables",
    description: "What specific deliverables do you expect from this project?",
    placeholder: "E.g., Mobile app with user authentication",
  },
  {
    field: "features",
    title: "Key Features",
    description: "What are the essential features your solution must have?",
    placeholder: "E.g., Real-time notifications",
  },
  {
    field: "risks",
    title: "Risks & Concerns",
    description: "What potential risks or concerns do you foresee with this project?",
    placeholder: "E.g., Tight timeline",
  },
  {
    field: "ownership",
    title: "Ownership & IP Rights",
    description: "What are your expectations regarding code ownership and intellectual property?",
    placeholder: "E.g., Full ownership of source code",
  },
  {
    field: "handoverProcess",
    title: "Handover Process",
    description: "How would you like the project handover to be managed?",
    placeholder: "E.g., Documentation and training sessions",
  },
  {
    field: "projectConstraints",
    title: "Project Constraints",
    description: "Are there any specific constraints or requirements we should be aware of?",
    placeholder: "E.g., Must use specific technologies",
  },
];

export function QuestionnaireStep({
  data,
  onChange,
  onNext,
  onBack,
}: QuestionnaireStepProps) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showReview, setShowReview] = useState(false);

  const currentQuestion = QUESTIONS[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === QUESTIONS.length - 1;
  const isCurrentQuestionValid = data[currentQuestion.field].length > 0;

  const handleFieldChange = (
    field: keyof QuestionnaireData,
    value: string[]
  ) => {
    onChange({ ...data, [field]: value });
  };

  const handleNextQuestion = () => {
    if (isLastQuestion) {
      setShowReview(true);
    } else {
      setCurrentQuestionIndex((prev) => prev + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
    } else {
      onBack();
    }
  };

  const handleEditQuestion = (index: number) => {
    setShowReview(false);
    setCurrentQuestionIndex(index);
  };

  if (showReview) {
    return (
      <Card className="glass-card border-gradient">
        <CardContent className="p-6 md:p-8 space-y-6">
          <div>
            <h2 className="font-display text-2xl font-bold mb-2">
              Review Your Answers
            </h2>
            <p className="text-muted-foreground">
              Review and edit your responses before continuing.
            </p>
          </div>

          <div className="space-y-6">
            {QUESTIONS.map((question, index) => (
              <div
                key={question.field}
                className="p-4 rounded-lg border bg-card/50"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-base flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-primary" />
                      {question.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {question.description}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditQuestion(index)}
                  >
                    Edit
                  </Button>
                </div>
                <ul className="space-y-2 ml-6">
                  {data[question.field].map((item, itemIndex) => (
                    <li key={itemIndex} className="text-sm flex items-start gap-2">
                      <span className="text-muted-foreground mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="flex justify-between pt-4">
            <Button
              onClick={() => setShowReview(false)}
              variant="outline"
              size="lg"
            >
              Back to Questions
            </Button>
            <Button onClick={onNext} size="lg">
              Continue to Meeting
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-card border-gradient">
      <CardContent className="p-6 md:p-8 space-y-6">
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Badge variant="outline">
                Question {currentQuestionIndex + 1} of {QUESTIONS.length}
              </Badge>
            </div>
            <div className="flex gap-1">
              {QUESTIONS.map((_, index) => (
                <div
                  key={index}
                  className={`h-2 w-8 rounded-full transition-colors ${
                    index <= currentQuestionIndex
                      ? "bg-primary"
                      : "bg-muted"
                  }`}
                />
              ))}
            </div>
          </div>
          <h2 className="font-display text-2xl font-bold mb-2">
            {currentQuestion.title}
          </h2>
          <p className="text-muted-foreground">
            {currentQuestion.description}
          </p>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion.field}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            <ListInput
              items={data[currentQuestion.field]}
              onChange={(items) =>
                handleFieldChange(currentQuestion.field, items)
              }
              placeholder={currentQuestion.placeholder}
            />
          </motion.div>
        </AnimatePresence>

        <div className="flex justify-between pt-4">
          <Button
            onClick={handlePreviousQuestion}
            variant="outline"
            size="lg"
          >
            Back
          </Button>
          <Button
            onClick={handleNextQuestion}
            disabled={!isCurrentQuestionValid}
            size="lg"
          >
            {isLastQuestion ? "Finish" : "Next"}
            {!isLastQuestion && <ChevronRight className="h-4 w-4 ml-2" />}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

